/**
 * usePasskeyManagement — canonical hook for WebAuthn passkey lifecycle
 * (list / register / delete).
 *
 * Standards-ID: MFA-FACTORY-001 (GEN0-SWEEP-001 Wave 2 Part B)
 *
 * Consolidates 5 per-app variants (patrol-web, palm-web, payday-web,
 * planner-web, purchase-web). Accepts `{ authService, webAuthnService }` as
 * DI props so each app provides its own service implementations:
 *   - `authService` must expose: `getPasskeys()`, `getPasskeyRegisterOptions()`,
 *     `registerPasskey(formatted, deviceName)`, `deletePasskey(id)`.
 *   - `webAuthnService` must expose: `prepareRegistrationOptions(opts)`,
 *     `createCredential(opts)`, `formatRegistrationResponse(credential)`.
 *
 * No isMountedRef ceremony — React 19 makes post-unmount setState a silent
 * no-op; see GUIDES/shared/standards/testing/react-19-unmount-semantics.md.
 *
 * Registration is the ONE ceremony, `enrollPasskey` (ECOSYSTEM-EMAIL-001 Q-EE-21 unit 1). A failure is held as an
 * `mfa` catalog KEY (`errorKey`), never as text: the card words it, so a Spanish person reads Spanish and a thrown
 * message (a server's log line, a browser's own text) never reaches the screen.
 *
 * Registration proves the person (ECOSYSTEM-EMAIL-001 Q-EE-34 P5 unit 4): every server's `/register/options` and
 * `/register` sit behind the host's binding of the ONE step-up seam, because a passkey is a complete sign-in on its own.
 * So the ceremony runs inside the host's REQUIRED `withStepUp(run, operationDescription)` — re-auth's `useStepUpAuth`
 * on a tenant app (a fresh credential proof, then ONE retry of the whole ceremony, so the options are fetched again
 * after the proof), or a pass-through where the transport asks for the proof itself (the console's `MFA_REQUIRED`).
 *
 * A passkey is a SECOND FACTOR (Q-EE-34 P5 unit 5): a removal passes the SAME proof (coordinator ruling Q-EE-45), and
 * the server may refuse it, each refusal worded by its own key: the only factor (`MFA_LAST_FACTOR`), an email code left
 * alone (`MFA_EMAIL_NEEDS_ANOTHER_FACTOR`), a list another change moved (`MFA_METHODS_CHANGED`). A registration that
 * turned MFA on answers backup codes once; the hook holds them until the person says they saved them.
 *
 * @module @puffin-core/mfa-ui/hooks/usePasskeyManagement
 */

import { useState, useEffect, useCallback } from 'react';

import {
  PASSKEY_ENROLLMENT_REFUSALS,
  PasskeyEnrollmentError,
  enrollPasskey,
} from '../enrollPasskey.js';
import { PASSKEY_CHALLENGE_STORE_UNAVAILABLE_CODE } from './useMfaRefusalText.js';

/** The catalog key each enrolment refusal is worded by; any other registration failure reads `passkey_errorRegister`. */
const REGISTER_REFUSAL_KEYS = Object.freeze({
  [PASSKEY_ENROLLMENT_REFUSALS.CANCELLED]: 'passkey_errorCancelled',
  [PASSKEY_ENROLLMENT_REFUSALS.ALREADY_REGISTERED]: 'passkey_errorAlreadyRegistered',
});

/**
 * Whether a registration ended because the person dismissed the proof the server asked for: re-auth's
 * `StepUpCancelledError` (its `name` is the contract — this package cannot import re-auth, which depends on it), or a
 * second-factor refusal the transport answered with the server's own denial once its dialog was dismissed.
 * Exported as the ONE judgement of a dismissed proof: the company MFA-policy editor words its own save by it too
 * (SECREQ S7 (a)), never a copy.
 *
 * @param {*} err
 * @returns {boolean}
 */
export function isProofDismissed(err) {
  return err?.name === 'StepUpCancelledError' || err?.errorCode === 'MFA_REQUIRED';
}

/** The catalog key each method-list refusal is worded by (Q-EE-34 P5 unit 5), on a registration or a removal. */
const METHOD_REFUSAL_KEYS = Object.freeze({
  MFA_LAST_FACTOR: 'passkey_errorLastFactor',
  MFA_EMAIL_NEEDS_ANOTHER_FACTOR: 'passkey_errorEmailAlone',
  MFA_METHODS_CHANGED: 'passkey_errorMethodsChanged',
});

/**
 * @param {*} err
 * @returns {string|undefined} the key a method-list refusal is worded by
 */
function methodRefusalKey(err) {
  return Object.hasOwn(METHOD_REFUSAL_KEYS, err?.errorCode)
    ? METHOD_REFUSAL_KEYS[err.errorCode]
    : undefined;
}

/**
 * The passkey router's codes this card words (D1 errorCode burn-down): a set-up challenge that expired or was used,
 * and a passkey that is no longer the person's (removed elsewhere). Its other refusals keep the card's general words.
 */
const PASSKEY_ROUTER_CODES = Object.freeze({
  CHALLENGE_NOT_FOUND: 'CHALLENGE_NOT_FOUND',
  PASSKEY_NOT_FOUND: 'PASSKEY_NOT_FOUND',
});

function removeErrorKey(err) {
  if (err?.errorCode === PASSKEY_ROUTER_CODES.PASSKEY_NOT_FOUND) return 'passkey_errorRemoveGone';
  const refusal = methodRefusalKey(err);
  if (refusal) return refusal;
  if (isProofDismissed(err)) return 'passkey_errorStepUpRemoveCancelled';
  return 'passkey_errorRemove';
}

/**
 * The ONE wording of a passkey REGISTRATION refusal: the catalog key a failed enrolment is told in, by the refusal's
 * class or code — never the browser's or the server's sentence. The in-session card reads it here; the held sign-in's
 * `<PasskeyEnrollment>` reads it too, adding only its own stale-proof case.
 *
 * @param {*} err - what the enrolment rejected with
 * @returns {string} an `mfa` catalog key
 */
export function registerErrorKey(err) {
  if (err instanceof PasskeyEnrollmentError) return REGISTER_REFUSAL_KEYS[err.code];
  const refusal = methodRefusalKey(err);
  if (refusal) return refusal;
  if (isProofDismissed(err)) return 'passkey_errorStepUpCancelled';
  if (err?.errorCode === PASSKEY_ROUTER_CODES.CHALLENGE_NOT_FOUND) return 'passkey_errorRegisterExpired';
  // The challenge store could not answer: the ONE sentence the MFA forms use for it (Q-EE-41).
  if (err?.errorCode === PASSKEY_CHALLENGE_STORE_UNAVAILABLE_CODE)
    return 'error_passkey_unavailable';
  return 'passkey_errorRegister';
}

const INITIAL_STATE = {
  passkeys: [],
  loading: true,
  errorKey: null,
  registering: false,
  deleting: false,
  deviceName: '',
  showRegisterForm: false,
  confirmDialog: { show: false, passkeyId: null, passkeyName: null },
  // The backup codes a first passkey's registration answered (it turned MFA on), shown until the person saves them.
  backupCodes: null,
  backupCodesAcknowledged: false,
};

/**
 * The delete-a-passkey flow: click → confirm dialog → delete. Module-private.
 *
 * The confirm step is a DESTRUCTIVE-ACTION GATE, not decoration: `handleDeleteClick`
 * only ever OPENS the dialog, and the credential is removed exclusively by
 * `handleConfirmDelete` reading `passkeyId` back out of `confirmDialog`. That
 * click → confirm → delete sequence is preserved exactly; a failure surfaces an
 * error and clears `deleting` (fail-closed — the dialog is not dismissed as if
 * the delete had succeeded).
 *
 * It shares the parent's single `state` object via `setState` rather than owning
 * its own slice, so the state SHAPE is unchanged.
 */
function usePasskeyDeleteFlow({
  confirmDialog,
  setState,
  authService,
  fetchPasskeys,
  withStepUp,
  stepUpRemoveOperation,
}) {
  const handleDeleteClick = useCallback(
    (passkeyId, passkeyName) => {
      setState((prev) => ({
        ...prev,
        confirmDialog: {
          show: true,
          passkeyId,
          passkeyName: passkeyName || 'Unnamed Passkey',
        },
      }));
    },
    [setState],
  );

  const handleConfirmDelete = useCallback(async () => {
    const { passkeyId } = confirmDialog;
    try {
      setState((prev) => ({ ...prev, deleting: true, errorKey: null }));
      // The removal passes the registration's proof (Q-EE-45).
      await withStepUp(() => authService.deletePasskey(passkeyId), stepUpRemoveOperation);
      setState((prev) => ({
        ...prev,
        confirmDialog: { show: false, passkeyId: null, passkeyName: null },
        deleting: false,
      }));
      await fetchPasskeys();
    } catch (err) {
      if (err?.errorCode === PASSKEY_ROUTER_CODES.PASSKEY_NOT_FOUND) {
        // Already removed (another device or tab): close the dialog and re-read the list, so the stale row goes, then
        // say so — after the re-read, which clears the card's error when it starts.
        setState((prev) => ({
          ...prev,
          confirmDialog: { show: false, passkeyId: null, passkeyName: null },
          deleting: false,
        }));
        await fetchPasskeys();
      }
      setState((prev) => ({ ...prev, errorKey: removeErrorKey(err), deleting: false }));
    }
  }, [confirmDialog, setState, fetchPasskeys, authService, withStepUp, stepUpRemoveOperation]);

  const handleCancelDelete = useCallback(() => {
    setState((prev) => ({
      ...prev,
      confirmDialog: { show: false, passkeyId: null, passkeyName: null },
    }));
  }, [setState]);

  return { handleDeleteClick, handleConfirmDelete, handleCancelDelete };
}

/**
 * Hook for managing passkey lifecycle operations.
 *
 * @param {Object} deps - Dependencies (DI for per-app service implementations)
 * @param {Object} deps.authService - App's auth service instance
 * @param {Object} deps.webAuthnService - App's WebAuthn service instance
 * @param {(run: () => Promise<*>, operationDescription: string) => Promise<*>} deps.withStepUp - REQUIRED: the host's
 *   step-up runner the registration ceremony runs inside (Q-EE-34 P5 unit 4)
 * @param {string} deps.stepUpOperation - the translated operation the proof prompt names ("add a passkey")
 * @param {string} deps.stepUpRemoveOperation - the one a removal's prompt names ("remove a passkey")
 * @returns {Object} Passkey management state and handlers
 * @throws {TypeError} when `withStepUp` is not a function (a host that wires none would ship a button that fails)
 */
export function usePasskeyManagement({
  authService,
  webAuthnService,
  withStepUp,
  stepUpOperation,
  stepUpRemoveOperation,
}) {
  if (typeof withStepUp !== 'function') {
    throw new TypeError(
      'usePasskeyManagement: withStepUp is required — every server asks for a fresh proof before a passkey is registered',
    );
  }
  const [state, setState] = useState({ ...INITIAL_STATE });

  const fetchPasskeys = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, loading: true, errorKey: null }));
      const result = await authService.getPasskeys();
      setState((prev) => ({ ...prev, passkeys: result.passkeys || [], loading: false }));
    } catch {
      setState((prev) => ({ ...prev, errorKey: 'passkey_errorLoad', loading: false }));
    }
  }, [authService]);

  useEffect(() => {
    fetchPasskeys();
  }, [fetchPasskeys]);

  const handleRegister = useCallback(async () => {
    setState((prev) => ({ ...prev, registering: true, errorKey: null }));
    let registered;
    try {
      // The WHOLE ceremony runs inside the proof: a retry after it fetches fresh options, never a stale challenge.
      registered = await withStepUp(
        () => enrollPasskey({ authService, webAuthnService, deviceName: state.deviceName }),
        stepUpOperation,
      );
    } catch (err) {
      setState((prev) => ({ ...prev, errorKey: registerErrorKey(err), registering: false }));
      return;
    }
    // A first passkey turned MFA on: its backup codes come back this once (Q-EE-34 P5 unit 5).
    const backupCodes = registered?.mfa?.backupCodes;
    setState((prev) => ({
      ...prev,
      deviceName: '',
      showRegisterForm: false,
      registering: false,
      ...(Array.isArray(backupCodes) && backupCodes.length > 0
        ? { backupCodes, backupCodesAcknowledged: false }
        : {}),
    }));
    await fetchPasskeys();
  }, [state.deviceName, fetchPasskeys, authService, webAuthnService, withStepUp, stepUpOperation]);

  const { handleDeleteClick, handleConfirmDelete, handleCancelDelete } = usePasskeyDeleteFlow({
    confirmDialog: state.confirmDialog,
    setState,
    authService,
    fetchPasskeys,
    withStepUp,
    stepUpRemoveOperation,
  });

  const handleBackupCodesAcknowledgeChange = useCallback((e) => {
    setState((prev) => ({ ...prev, backupCodesAcknowledged: e.target.checked }));
  }, []);

  // The person saved the codes: they leave the screen for good (the server keeps only their hashes).
  const handleBackupCodesSaved = useCallback(() => {
    setState((prev) => ({ ...prev, backupCodes: null, backupCodesAcknowledged: false }));
  }, []);

  const handleToggleRegisterForm = useCallback(() => {
    setState((prev) => ({
      ...prev,
      showRegisterForm: !prev.showRegisterForm,
      deviceName: '',
    }));
  }, []);

  const handleDeviceNameChange = useCallback((e) => {
    setState((prev) => ({ ...prev, deviceName: e.target.value }));
  }, []);

  return {
    passkeys: state.passkeys,
    loading: state.loading,
    errorKey: state.errorKey,
    registering: state.registering,
    deleting: state.deleting,
    deviceName: state.deviceName,
    showRegisterForm: state.showRegisterForm,
    confirmDialog: state.confirmDialog,
    backupCodes: state.backupCodes,
    backupCodesAcknowledged: state.backupCodesAcknowledged,
    handleBackupCodesAcknowledgeChange,
    handleBackupCodesSaved,
    fetchPasskeys,
    handleRegister,
    handleDeleteClick,
    handleConfirmDelete,
    handleCancelDelete,
    handleToggleRegisterForm,
    handleDeviceNameChange,
  };
}
